/**
 * The platform console's Acquisition learning tab (Acquisition P7): the parameter corrections persons made the same way
 * at several organisations while reviewing acquisition studies (a point's name set to the same pack parameter where the
 * plan proposed something else), each with what the foundry pack would need for the matcher to propose it itself. Read
 * only: a person turns a proposal into a pack PR, and is told not to copy a plant's own names into the pack. It names
 * no organisation and shows no value, only the points' folded name; a name that carries an organisation's own name is
 * left out by the server. Read from GET /v1/platform/acquisition/learning (platform admins).
 *
 * AcquisitionLearningView is pure (rendered in tests without providers); AcquisitionLearningPanel holds the read.
 */
import { useState } from "react";
import { GraduationCap, Loader2 } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";
import { NativeSelect } from "@/lib/ui/native-select";
import { backendErrorMessage } from "@/lib/api";
import { useAcquisitionLearningQuery, type AcquisitionLearning } from "@/lib/api/acquisition-points";
import {
  DEFAULT_MIN_ORGS,
  MIN_ORGS_CHOICES,
  NEED_LABELS,
  OWN_NAMES_WORDS,
  listedProposals,
  minOrgsWords,
  targetWords,
} from "./acquisition-learning";

const TH = "whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-on-surface-muted";
const TD = "px-3 py-2 align-top text-sm";

export interface AcquisitionLearningViewProps {
  minOrgs: number;
  onMinOrgs: (n: number) => void;
  state: "loading" | "failed" | "ready";
  error?: string;
  data?: AcquisitionLearning;
}

export function AcquisitionLearningView({ minOrgs, onMinOrgs, state, error, data }: AcquisitionLearningViewProps) {
  const [showAll, setShowAll] = useState(false);
  const proposals = data?.proposals ?? [];
  const listed = listedProposals(proposals, showAll);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GraduationCap className="size-4" aria-hidden /> Acquisition learning
        </CardTitle>
        <p className="text-sm text-on-surface-muted">
          Parameter corrections persons made the same way at several organisations while reviewing acquisition studies,
          each with what the foundry pack would need to propose it itself. Read only: copy a proposal into a pack PR. No
          organisation is named and no value is shown, and a name that carries an organisation&apos;s own name is left
          out.
        </p>
        <p className="text-sm font-medium text-on-surface">{OWN_NAMES_WORDS}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <label className="flex w-fit flex-col gap-1 text-xs text-on-surface-muted">
          Made at
          <NativeSelect
            aria-label="Made at"
            className="h-9 w-56"
            value={String(minOrgs)}
            onChange={(e) => onMinOrgs(Number(e.target.value))}
          >
            {MIN_ORGS_CHOICES.map((n) => (
              <option key={n} value={n}>
                {minOrgsWords(n)}
              </option>
            ))}
          </NativeSelect>
        </label>
        {state === "loading" ? (
          <p role="status" className="flex items-center gap-2 text-sm text-on-surface-muted">
            <Loader2 aria-hidden className="size-4 animate-spin" /> Reading the corrections
          </p>
        ) : state === "failed" ? (
          <p role="alert" className="text-sm text-danger">
            {error || "The corrections could not be read."}
          </p>
        ) : !data ? (
          <p className="text-sm text-on-surface-muted">
            The corrections are read from the live platform: sign in to see them.
          </p>
        ) : proposals.length === 0 ? (
          <p className="text-sm text-on-surface-muted">
            No correction was made the same way at {minOrgsWords(minOrgs)} yet, of{" "}
            {data.corrections.toLocaleString("en-US")} read.
          </p>
        ) : (
          <>
            <p className="text-xs text-on-surface-muted">
              {proposals.length.toLocaleString("en-US")} {proposals.length === 1 ? "proposal" : "proposals"} from{" "}
              {data.corrections.toLocaleString("en-US")} corrections read
              {data.truncated ? ", the newest only" : ""}.
            </p>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full">
                <caption className="sr-only">
                  Parameter corrections made the same way at {minOrgsWords(minOrgs)}, with what the pack needs
                </caption>
                <thead className="bg-surface-muted/40">
                  <tr>
                    <th scope="col" className={TH}>
                      Name
                    </th>
                    <th scope="col" className={TH}>
                      Set to
                    </th>
                    <th scope="col" className={TH}>
                      Organisations
                    </th>
                    <th scope="col" className={TH}>
                      What the pack needs
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {listed.map((p) => (
                    <tr key={`${p.fold}|${p.param}`} className="border-t border-border">
                      <td className={`${TD} font-mono text-xs`}>{p.fold}</td>
                      <td className={TD}>{targetWords(p)}</td>
                      <td className={TD}>{p.orgs.toLocaleString("en-US")}</td>
                      <td className={TD}>
                        <span className="flex flex-col gap-1">
                          <Badge variant={p.need === "none" ? "secondary" : "info"} className="w-fit">
                            {NEED_LABELS[p.need]}
                          </Badge>
                          <span className="text-on-surface">{p.words}</span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {listed.length < proposals.length && (
              <div className="flex flex-wrap items-center gap-3" data-proposals-more="">
                <span className="text-xs text-on-surface-muted">
                  Showing {listed.length.toLocaleString("en-US")} of {proposals.length.toLocaleString("en-US")}.
                </span>
                <Button size="sm" variant="secondary" onClick={() => setShowAll(true)}>
                  Show all
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** The tab with its read. */
export function AcquisitionLearningPanel() {
  const [minOrgs, setMinOrgs] = useState<number>(DEFAULT_MIN_ORGS);
  const q = useAcquisitionLearningQuery(minOrgs);
  return (
    <AcquisitionLearningView
      minOrgs={minOrgs}
      onMinOrgs={setMinOrgs}
      state={q.isLoading ? "loading" : q.isError ? "failed" : "ready"}
      error={q.isError ? backendErrorMessage(q.error) : undefined}
      data={q.data}
    />
  );
}
